/**
 * O wizard de ambiente novo, em modal, sobre o MESMO rascunho que o chat grava
 * (spec 2026-10-09 §5, Abordagem A da D4).
 *
 * - Abre só por botão de cartão — nunca sozinho: o "Revisar e confirmar" da
 *   criação, ou o "Abrir o formulário" que nasce com o rascunho
 *   (consoleai#25762). Aberto pelo segundo, confirma quando o servidor diz que
 *   há cartão de criação e quem vê pode (`cartao_de_criacao`, consoleai#26021):
 *   o cartão recebe esse dado por `onCartaoDeCriacao` e passa `onConfirmar`.
 *   Sem `onConfirmar`, a Revisão diz o motivo, ou onde o Confirmar mora.
 * - Fechar SALVA (espera a fila sair) e não descarta; descartar não mora aqui,
 *   é a ferramenta do chat, com motivo.
 * - Cada gravação posta uma linha na conversa (servidor); a gravação do
 *   @Atendente faz o modal reler (`useRascunho`).
 * - Celular: tela cheia, edita; o Confirmar dá lugar à frase.
 * - Foco preso no modal (padrão do Mantine), `aria-live` para "salvo" e
 *   leituras. Gradiente só no Confirmar (o botão primário cheio do tema); os
 *   demais botões não são primários cheios.
 */
import { useEffect, useState } from 'react'
import { Alert, Button, Group, Loader, Modal, Stack, Stepper, Text } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { IconAlertTriangle, IconChevronLeft, IconChevronRight } from '@tabler/icons-react'
import { Dado } from '../components/Dado'
import type { CartaoDeCriacao } from '@/api/rascunhoDeAmbiente'
import {
  bloqueiosDoRascunho, PassoBanco, PassoProjetoRede, PassoQuemAcompanha, PassoRevisao,
  PassoServicos, PassoTenant, type Bloqueio, type PassoProps,
} from './Passos'
import { bloqueiosDaProntidao, PassoProntidao, useProntidao } from './PassoProntidao'
import { useRascunho, type EstadoDaGravacao } from './useRascunho'

const BARLOW = { fontFamily: '"Barlow", Arial, sans-serif' }

const TEXTO_DO_ESTADO: Record<EstadoDaGravacao, string> = {
  salvo: 'Salvo',
  pendente: 'Alterações a salvar…',
  salvando: 'Salvando…',
  erro: 'Não salvo',
}

export interface WizardAmbienteProps {
  aberto: boolean
  onFechar: () => void
  tenantId: number
  rascunhoId: number
  /** O servidor decidiu que quem vê pode confirmar (o mesmo gate do cartão). */
  podeConfirmar: boolean
  /** Por que não (precondição do ambiente, permissão), quando o servidor disse.
   *  Sem `onConfirmar`, é a frase do rodapé da Revisão. */
  motivoBloqueio?: string | null
  /** Sem ele, o wizard não confirma: o rodapé diz o motivo ou onde o Confirmar mora. */
  onConfirmar?: () => void
  confirmando?: boolean
  /** O `cartao_de_criacao` de cada leitura/gravação do rascunho: o PUT que o
   *  completa traz o cartão, e o Confirmar aparece sem fechar o modal. */
  onCartaoDeCriacao?: (cartao: CartaoDeCriacao | null) => void
  /** A chave do passo em que o wizard abre (`formulario.PASSOS`): a conversa em
   *  etapas abre um passo por vez (consoleai#25981). Ausente ou desconhecida, o
   *  primeiro. */
  passoInicial?: string
}

export function WizardAmbiente(props: WizardAmbienteProps) {
  const { aberto, onFechar, tenantId, rascunhoId } = props
  const celular = useMediaQuery('(max-width: 768px)') ?? false
  // Os 7 rótulos pedem ~900px numa linha (medido em 29/10/2026, consoleai#25935):
  // abaixo de 1200px só o passo aberto mostra o nome, e os outros o número.
  const telaLarga = useMediaQuery('(min-width: 75em)') ?? false
  const r = useRascunho(tenantId, rascunhoId, aberto)
  // `undefined` (réplica antiga, ou ainda lendo) não é "sem cartão": não avisa.
  // A chave é o conteúdo: a releitura igual não avisa de novo.
  const cartao = r.rascunho?.cartao_de_criacao
  const chaveDoCartao = cartao === undefined ? undefined : JSON.stringify(cartao)
  const { onCartaoDeCriacao } = props
  useEffect(() => {
    if (chaveDoCartao !== undefined) onCartaoDeCriacao?.(JSON.parse(chaveDoCartao))
  }, [chaveDoCartao, onCartaoDeCriacao])
  const [passo, setPasso] = useState(0)
  // Os passos chegam com o rascunho: o passo pedido pelo cartão só se resolve
  // depois, e vale uma vez por abertura — depois quem anda é a pessoa.
  const [passoAplicado, setPassoAplicado] = useState(false)
  // O tenant DO RASCUNHO: trocar o ambiente o move de tenant (consoleai#25804),
  // e o do cartão que abriu o modal fica para trás.
  const tenantDoRascunho = r.rascunho?.tenant.id ?? tenantId
  // A prontidão é lida pelo wizard, não pelo passo: pular direto para a Revisão
  // não deixa o Confirmar livre com a conta sem conferir. Com o tenant na
  // chave, trocar o ambiente reabre a conferência na conta nova.
  const prontidao = useProntidao(tenantDoRascunho, rascunhoId, aberto)
  const [fechando, setFechando] = useState(false)
  const [avisoDeConflito, setAvisoDeConflito] = useState(false)

  // Com conflito aberto, a fila não sai (ela espera "manter o meu"/"usar o
  // novo"): fechar agora perderia a edição em silêncio. Avisa primeiro.
  const fechar = async () => {
    if (r.conflito) {
      setAvisoDeConflito(true)
      return
    }
    await fecharDeVez()
  }

  const descartarOMeuEFechar = async () => {
    r.usarONovo()
    setAvisoDeConflito(false)
    await fecharDeVez()
  }

  const fecharDeVez = async () => {
    setFechando(true)
    try {
      await r.salvar()
    } finally {
      setFechando(false)
      onFechar()
    }
  }

  const bloqueios: Bloqueio[] = [
    ...bloqueiosDoRascunho(r),
    ...bloqueiosDaProntidao(prontidao),
    ...(props.onConfirmar && !props.podeConfirmar && props.motivoBloqueio
      ? [{ chave: 'servidor', texto: props.motivoBloqueio }] : []),
  ]
  const salvoSemConflito = r.estado === 'salvo' && !r.conflito
  const podeClicar = props.podeConfirmar && bloqueios.length === 0 && salvoSemConflito

  const confirmar = async () => {
    await r.salvar()
    props.onConfirmar?.()
    onFechar()
  }

  const passoProps: PassoProps = { r, tenantId: tenantDoRascunho, rascunhoId }
  // Os passos vêm do servidor (`formulario.PASSOS`): o mesmo nome que o
  // @Atendente diz quando aponta o passo de um campo (consoleai#25804).
  const passos = r.rascunho?.passos ?? []
  useEffect(() => {
    if (!aberto) {
      setPassoAplicado(false)
      return
    }
    if (passoAplicado || passos.length === 0) return
    const i = passos.findIndex((p) => p.chave === props.passoInicial)
    setPasso(i >= 0 ? i : 0)
    setPassoAplicado(true)
  }, [aberto, passoAplicado, passos, props.passoInicial])
  const chave = passos[passo]?.chave
  const titulo = r.rascunho
    ? <Text fw={700} style={BARLOW}>Rascunho #{r.rascunho.id} · <Dado>{r.rascunho.projeto}/{r.rascunho.ambiente}</Dado></Text>
    : <Text fw={700} style={BARLOW}>Rascunho #{rascunhoId}</Text>

  return (
    <Modal opened={aberto} onClose={() => { void fechar() }} title={titulo}
           fullScreen={celular} size="70rem" trapFocus closeOnClickOutside={false}
           closeButtonProps={{ 'aria-label': 'Fechar e salvar' }}>
      <Stack gap="md">
        <Group justify="space-between" wrap="wrap" gap="xs">
          <Text size="xs" c="dimmed" style={BARLOW} aria-live="polite" role="status">
            {TEXTO_DO_ESTADO[r.estado]}
          </Text>
          <Text size="xs" c="dimmed" style={BARLOW}>Fechar salva; nada é descartado.</Text>
        </Group>

        {r.mensagemDeErro && !r.conflito && (
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} stroke={1.5} />} role="alert">
            {r.mensagemDeErro}
          </Alert>
        )}

        {r.conflito && avisoDeConflito && (
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} stroke={1.5} />} role="alert">
            <Stack gap={6}>
              <Text size="sm" style={BARLOW}>
                Há uma alteração sua em conflito: resolva ou descarte antes de fechar.
              </Text>
              <Group gap="xs">
                <Button size="xs" variant="default" onClick={() => { void descartarOMeuEFechar() }}>
                  Descartar a minha e fechar
                </Button>
              </Group>
            </Stack>
          </Alert>
        )}

        {r.conflito && (
          <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} stroke={1.5} />}
                 title="O rascunho mudou enquanto você editava" role="alert">
            <Stack gap={6}>
              {Object.entries(r.conflito.campos).map(([campo, d]) => (
                <Text key={campo} size="sm" style={BARLOW}>
                  <Dado>{campo}</Dado>: seu <Dado>{String(d.meu)}</Dado> · gravado <Dado>{String(d.gravado)}</Dado>
                </Text>
              ))}
              <Group gap="xs">
                <Button size="xs" variant="default" onClick={() => { void r.manterOMeu() }}>Manter o meu</Button>
                <Button size="xs" variant="default" onClick={r.usarONovo}>Usar o novo</Button>
              </Group>
            </Stack>
          </Alert>
        )}

        {r.carregando && (
          <Group gap={6}><Loader size={14} aria-hidden /><Text size="sm" style={BARLOW}>Lendo o rascunho…</Text></Group>
        )}
        {r.erroDeLeitura && (
          <Alert color="red" variant="light" role="alert">Não consegui ler o rascunho agora.</Alert>
        )}

        {r.rascunho && (
          <>
            <Stepper active={passo} onStepClick={setPasso} size="xs"
                     orientation={celular ? 'vertical' : 'horizontal'} allowNextStepsSelect
                     styles={celular ? undefined : {
                       steps: { flexWrap: 'nowrap' },
                       stepLabel: { whiteSpace: 'nowrap' },
                       separator: { marginInline: 8, minWidth: 8 },
                     }}>
              {passos.map((p, i) => (
                <Stepper.Step key={p.chave} aria-label={p.nome} title={p.nome}
                              label={celular || telaLarga || i === passo ? p.nome : undefined} />
              ))}
            </Stepper>

            <section aria-label={passos[passo]?.nome}>
              {chave === 'ambiente' && <PassoTenant {...passoProps} />}
              {chave === 'prontidao' && <PassoProntidao {...passoProps} prontidao={prontidao} />}
              {chave === 'projeto' && <PassoProjetoRede {...passoProps} />}
              {chave === 'servicos' && <PassoServicos {...passoProps} />}
              {chave === 'banco' && <PassoBanco {...passoProps} />}
              {chave === 'acompanhantes' && <PassoQuemAcompanha {...passoProps} />}
              {chave === 'revisao' && <PassoRevisao {...passoProps} bloqueios={bloqueios} />}
            </section>

            <Group justify="space-between" wrap="wrap" gap="xs">
              <Button variant="subtle" leftSection={<IconChevronLeft size={16} stroke={1.5} />}
                      disabled={passo === 0} onClick={() => setPasso((p) => Math.max(0, p - 1))}>
                Voltar
              </Button>
              {passo < passos.length - 1 ? (
                <Button variant="default" rightSection={<IconChevronRight size={16} stroke={1.5} />}
                        onClick={() => setPasso((p) => Math.min(passos.length - 1, p + 1))}>
                  Próximo
                </Button>
              ) : !props.onConfirmar ? (
                <Text size="sm" style={BARLOW}>
                  {props.motivoBloqueio
                    ? `Ainda não dá para confirmar: ${props.motivoBloqueio}`
                    : 'O Confirmar fica no cartão de criação, que o @Atendente traz quando o rascunho estiver completo.'}
                </Text>
              ) : celular ? (
                <Text size="sm" style={BARLOW}>Confirmar só no computador; o pedido fica salvo.</Text>
              ) : (
                <Button variant="filled" color="inovareTeal" disabled={!podeClicar}
                        loading={props.confirmando || fechando} onClick={() => { void confirmar() }}>
                  Confirmar
                </Button>
              )}
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  )
}
